import React, { useState, useRef, useEffect } from "react";
import { X, Plus, Search } from "lucide-react";
import { FaStar } from "react-icons/fa";
import { availableFeatures } from "./availableFeatures";
import { UserListing } from "../../../types/userListing";

interface FeatureSelectorProps {
    listing: UserListing;
    setListing: React.Dispatch<React.SetStateAction<UserListing | null>>;
}

const PropertyFeatureSelector = ({
    listing,
    setListing,
}: FeatureSelectorProps) => {
    const features = listing?.features;
    let featureKeys: string[] = [];
    let featureValues: (string | number | boolean)[] = [];
    let nonMLSStringFeatures: string[] = [];
    let nonMLSNumberFeatures: string[] = [];

    if (features) {
        Object.keys(features).forEach((key) => {
            featureKeys.push(key);
        });
        Object.values(features).forEach((value) => {
            featureValues.push(value);
        });

        featureKeys.forEach((key, index) => {
            if (typeof featureValues[index] === "string") {
                nonMLSStringFeatures.push(`${key}: ${featureValues[index]}`);
            } else if (typeof featureValues[index] === "number") {
                nonMLSNumberFeatures.push(`${key}: ${featureValues[index]}`);
            }
        });
    }

    const [selectedFeatures, setSelectedFeatures] = useState<string[]>([
        ...nonMLSStringFeatures,
    ]);

    const [searchTerm, setSearchTerm] = useState("");
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [filteredSuggestions, setFilteredSuggestions] = useState<string[]>(
        []
    );
    const inputRef = useRef<HTMLInputElement | null>(null);
    const containerRef = useRef<HTMLDivElement | null>(null);

    // Filter suggestions based on search term and exclude already selected features
    useEffect(() => {
        if (searchTerm.trim() === "") {
            setFilteredSuggestions([]);
            return;
        }

        const filtered = availableFeatures
            .filter(
                (feature) =>
                    !selectedFeatures.includes(feature) &&
                    feature.toLowerCase().includes(searchTerm.toLowerCase())
            )
            .slice(0, 8); // Limit to 8 suggestions

        setFilteredSuggestions(filtered);
    }, [searchTerm, selectedFeatures]);

    // Handle clicking outside to close suggestions
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                containerRef.current &&
                event.target instanceof Node &&
                !containerRef.current.contains(event.target)
            ) {
                setShowSuggestions(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () =>
            document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchTerm(e.target.value);
        setShowSuggestions(true);
    };

    const handleInputFocus = () => {
        setShowSuggestions(true);
    };

    const addFeature = (feature: string) => {
        if (!selectedFeatures.includes(feature)) {
            setSelectedFeatures([...selectedFeatures, feature]);
            handleFeatureChange([...selectedFeatures, feature]);
        }
        setSearchTerm("");
        setShowSuggestions(false);
        inputRef.current?.focus();
    };

    const removeFeature = (featureToRemove: string) => {
        setSelectedFeatures(
            selectedFeatures.filter((feature) => feature !== featureToRemove)
        );
        handleFeatureChange(
            selectedFeatures.filter((feature) => feature !== featureToRemove)
        );
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Enter" && filteredSuggestions.length > 0) {
            e.preventDefault();
            addFeature(filteredSuggestions[0]);
        } else if (e.key === "Escape") {
            setShowSuggestions(false);
            setSearchTerm("");
        }
    };

    // Format feature labels by inserting a space before mid-word capital letters
    // e.g., "CoolingType" -> "Cooling Type"
    const formatFeatureLabel = (label: string) => {
        if (!label) return label;
        const spaced = label.replace(/([a-z])([A-Z])/g, "$1 $2");
        return spaced.charAt(0).toUpperCase() + spaced.slice(1);
    };

    const handleFeatureChange = (selectedFeatures: string[]) => {
        if (!listing) return;

        // Parse features to separate key and value
        const updatedFeatures = { ...listing.features } as any;

        // Remove all non-MLS features first (clear existing custom features)
        Object.keys(updatedFeatures).forEach((key) => {
            if (
                nonMLSStringFeatures.some((feature) =>
                    feature.startsWith(`${key}:`)
                ) ||
                nonMLSNumberFeatures.some((feature) =>
                    feature.startsWith(`${key}:`)
                )
            ) {
                delete updatedFeatures[key];
            }
        });

        // Add new selected features
        selectedFeatures.forEach((feature) => {
            if (feature.includes(":")) {
                const [key, value] = feature
                    .split(":")
                    .map((part) => part.trim());
                if (key && value) {
                    // Try to parse as number, otherwise keep as string
                    const numericValue = parseFloat(value);
                    updatedFeatures[key] = isNaN(numericValue)
                        ? value
                        : numericValue;
                }
            }
        });

        console.log(updatedFeatures);

        setListing((prev) =>
            prev
                ? {
                      ...prev,
                      features: updatedFeatures,
                  }
                : prev
        );
    };

    return (
        <>
            <div className="mb-6">
                <h3 className="text-lg font-semibold mb-2 flex items-center gap-2 text-primary">
                    <FaStar className="h-6 w-6 text-secondary" />
                    Features
                </h3>
            </div>

            {/* Selected Features */}
            <div className="mb-6">
                <div className="flex flex-wrap gap-2">
                    {selectedFeatures.map((feature, index) => (
                        <div
                            key={index}
                            className="inline-flex items-center bg-lightSecondary text-yellow-800 px-3 py-1 rounded-full text-sm font-medium"
                        >
                            {formatFeatureLabel(feature)}
                            <button
                                onClick={() => removeFeature(feature)}
                                className="ml-2 text-yellow-600 hover:text-yellow-800 focus:outline-none"
                                aria-label={`Remove ${feature}`}
                            >
                                <X size={14} />
                            </button>
                        </div>
                    ))}
                </div>
            </div>

            {/* Add Feature Input */}
            <div className="relative" ref={containerRef}>
                <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <Search className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                        ref={inputRef}
                        type="text"
                        value={searchTerm}
                        onChange={handleInputChange}
                        onFocus={handleInputFocus}
                        onKeyDown={handleKeyDown}
                        placeholder="Search and add features..."
                        className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg outline-none text-gray-700"
                    />
                </div>

                {/* Suggestions Dropdown */}
                {showSuggestions &&
                    (searchTerm.trim() !== "" ||
                        filteredSuggestions.length > 0) && (
                        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-64 overflow-y-auto">
                            {filteredSuggestions.length > 0 ? (
                                <>
                                    <div className="px-3 py-2 text-xs font-semibold text-gray-500 bg-gray-50 border-b">
                                        Suggested Features
                                    </div>
                                    {filteredSuggestions.map(
                                        (feature, index) => (
                                            <button
                                                key={index}
                                                onClick={() =>
                                                    addFeature(feature)
                                                }
                                                className="w-full text-left px-3 py-2 hover:bg-blue-50 focus:bg-blue-50 focus:outline-none text-gray-700 flex items-center"
                                            >
                                                <Plus className="h-4 w-4 text-green-500 mr-2 flex-shrink-0" />
                                                {formatFeatureLabel(feature)}
                                            </button>
                                        )
                                    )}
                                </>
                            ) : searchTerm.trim() !== "" ? (
                                <div className="px-3 py-4 text-center text-gray-500">
                                    No matching features found
                                </div>
                            ) : null}
                        </div>
                    )}
            </div>

            {/* Helper Text */}
            <p className="mt-3 text-sm text-gray-600">
                Start typing to search for additional features to add to this
                property.
            </p>

            {/* Stats */}
            <div className="mt-6 pt-4 border-t border-gray-100">
                <p className="text-sm text-gray-500">
                    {selectedFeatures.length} features selected •{" "}
                    {availableFeatures.length - selectedFeatures.length} more
                    available
                </p>
            </div>
        </>
    );
};

export default PropertyFeatureSelector;
